import { getDocument } from "@faire/globals/browser/getDocument";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "area[href]",
  "button:not([disabled])",
  'input:not([disabled]):not([type="hidden"])',
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(",");

/**
 * Focuses the first focusable descendant of `root`. Returns `true` if a
 * focusable element was found and focused, `false` otherwise.
 *
 * Does not filter by CSS visibility — a `display: none` / `visibility: hidden`
 * candidate is still selected, and `focus()` silently no-ops on it. Avoid
 * passing roots whose first focusable descendant is CSS-hidden.
 */
export const focusFirst = (
  root: HTMLElement,
  options?: FocusOptions
): boolean => {
  const target = root.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
  if (target === null) {
    return false;
  }
  target.focus(options);
  return true;
};

/**
 * Finds the next focusable element after `root` in DOM order, falling back to
 * the previous focusable element. Returns `null` if the document has no other
 * focusables.
 */
export const findFocusTargetOutside = (
  root: HTMLElement
): HTMLElement | null => {
  const doc = getDocument();
  if (!doc) {
    return null;
  }
  const all = Array.from(
    doc.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
  ).filter((el) => !root.contains(el));

  const next = all.find(
    (el) =>
      (root.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING) !==
      0
  );
  if (next) {
    return next;
  }

  for (let i = all.length - 1; i >= 0; i--) {
    const candidate = all[i];
    if (
      candidate &&
      (root.compareDocumentPosition(candidate) &
        Node.DOCUMENT_POSITION_PRECEDING) !==
        0
    ) {
      return candidate;
    }
  }
  return null;
};
